import * as cx from 'classnames';
import FormControl from 'mk2/components/forms/FormControl';
import { AutoFocus, autoFocusElement, normalizeToInt } from 'mk2/helpers/form';
import * as React from 'react';
import { EventOrValueHandler, Field, Normalizer, WrappedFieldInputProps, WrappedFieldProps } from 'redux-form';
import styles from './InputField.mscss';

type TextInputType = 'text' | 'radio' | 'date' | 'email' | 'number' | 'tel' | 'url';

interface TextInputPublicProps {
    type: TextInputType;
    autoFocus?: AutoFocus;
    autoCorrect?: string;
    autoCapitalize?: string;
    spellCheck?: boolean;
    maxLength?: number;
    placeholder?: string;
    disabled?: boolean;
}

type TextInputOwnProps = TextInputPublicProps & {
    input: WrappedFieldInputProps;

    onAutofill?();
};

class TextInput extends React.Component<TextInputOwnProps> {

    private inputElem: HTMLInputElement;

    private autofillTriggered: () => void;

    constructor(props) {
            super(props);
            this.autofillTriggered = () => { this.labelUpdate(); };
        }

    public render() {
        const {
            input, type, autoFocus, autoCorrect, autoCapitalize, spellCheck,
            maxLength, placeholder, disabled,
        } = this.props;

        return (
            <input
                id={input.name}
                className={cx(
                    styles.TextInput,
                    !!disabled && styles['TextInput--disabled'],
                )}
                type={type}
                maxLength={maxLength}
                autoCorrect={autoCorrect}
                autoCapitalize={autoCapitalize}
                autoFocus={!!autoFocus}
                spellCheck={spellCheck}
                placeholder={placeholder}
                disabled={!!disabled}

                {...input}
                ref={this.handleInputRef}
            />
        );
    }

    public componentDidMount() {
        autoFocusElement(this.inputElem, this.props.autoFocus, this.props.input.value);
        window.addEventListener('transitionend', this.autofillTriggered);
    }

    public componentWillUnmount() {
        window.removeEventListener('transitionend', this.autofillTriggered);
    }

    private handleInputRef = (ref: HTMLInputElement) => {
        this.inputElem = ref;
    };

    private labelUpdate() {
        if (this.props.onAutofill) {
            this.props.onAutofill();
        }
    }
}

interface TextControlPublicProps {
    classNames?: {  // classNames for inner components
        label?: string;
    };
    className?: string;
    label?: string;
    minimizedLabel?: boolean;
    marginLeft?: boolean;
    bottomBorder?: boolean;
    dangerouslySetInnerHTMLForError?: boolean;
    transparent?: boolean;

    onAutofill?();
}

type TextControlProps = TextInputPublicProps & TextControlPublicProps & WrappedFieldProps;

const TextControl: React.StatelessComponent<TextControlProps> = ({
    // GroupProps
    className, minimizedLabel, marginLeft, bottomBorder, dangerouslySetInnerHTMLForError, classNames, transparent, onAutofill,
    // WrappedFieldProps
    input, meta, label,
    // InputProps
    ...textInputProps
}) => (
    <FormControl
        className={className}
        input={input}
        meta={meta}
        label={label}
        minimizedLabel={
            minimizedLabel
            || textInputProps.type === 'date'
            || textInputProps.type === 'number'
            || !!textInputProps.placeholder
        }
        marginLeft={marginLeft}
        bottomBorder={bottomBorder}
        dangerouslySetInnerHTMLForError={dangerouslySetInnerHTMLForError}
        transparent={transparent}
    >
        <TextInput
            input={input}
            onAutofill={onAutofill}
            {...textInputProps}
        />
    </FormControl>
);

type TextInputFieldProps = TextInputPublicProps & TextControlPublicProps & {
    // see https://redux-form.com/7.3.0/docs/api/field.md/#props-you-can-pass-to-code-field-code-
    name: string;
    normalize?: Normalizer;
    onChange?: EventOrValueHandler<React.ChangeEvent<any>>;
};

const TextInputField: React.StatelessComponent<TextInputFieldProps> = (props) => (
    <Field
        {...props}
        normalize={props.normalize || (props.type === 'number' ? normalizeToInt : null)}
        component={TextControl}
    />
);

export default TextInputField;
